import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { DotsSixVertical } from "@phosphor-icons/react";

function SortableItem({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: "relative",
    zIndex: isDragging ? 50 : "auto",
  };
  return (
    <div ref={setNodeRef} style={style} className="flex items-stretch gap-0">
      <div {...attributes} {...listeners} className="flex items-center px-1.5 cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 shrink-0" data-testid={`drag-handle-${id}`}>
        <DotsSixVertical size={16} />
      </div>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

export function SortableList({ items, onReorder, getId, children }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = items.map((item, i) => getId ? getId(item, i) : (item.id || `item-${i}`));
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex !== -1 && newIndex !== -1) {
      onReorder(arrayMove([...items], oldIndex, newIndex));
    }
  };

  const ids = items.map((item, i) => getId ? getId(item, i) : (item.id || `item-${i}`));

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} modifiers={[restrictToVerticalAxis]}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className="space-y-2">
          {items.map((item, idx) => (
            <SortableItem key={ids[idx]} id={ids[idx]}>
              {children(item, idx)}
            </SortableItem>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

export { SortableItem };
